import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { fullName } from '@pickem/shared';
import { Wordmark } from './Brand';
import { LeagueSwitcher } from './LeagueSwitcher';
import { useAuth } from '../lib/auth';

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout, isLeagueStaff, activeLeagueId } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-full">
      <header className="bg-grove text-white shadow-card">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <Wordmark />
          <nav className="flex items-center gap-1 text-sm">
            <TopLink to="/">Home</TopLink>
            <TopLink to="/standings">Standings</TopLink>
            {isLeagueStaff && <TopLink to="/admin">Admin</TopLink>}
            <LeagueSwitcher />
          </nav>
        </div>
        {user && (
          <div className="border-t border-white/10 bg-grove-dark">
            <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-1.5 text-xs text-white/80">
              <span className="truncate">
                Signed in as <span className="font-semibold text-white">{fullName(user)}</span>
              </span>
              <div className="flex items-center gap-3">
                <NavLink to="/account" className="hover:text-white">
                  Account
                </NavLink>
                <button
                  type="button"
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                  className="hover:text-white"
                >
                  Log out
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
      <main key={activeLeagueId ?? 'none'} className="mx-auto max-w-5xl px-4 py-6">{children}</main>
      <footer className="mx-auto max-w-5xl px-4 pb-10 pt-4 text-center text-xs text-muted">
        Lost Grove Pick&nbsp;’Em · Lost Grove Brewing · Boise, Idaho
      </footer>
    </div>
  );
}

function TopLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `rounded px-3 py-1.5 font-medium transition-colors ${
          isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

/** Narrower shell for the mobile-first pick flow. */
export function FocusShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-full">
      <header className="bg-grove text-white">
        <div className="mx-auto flex max-w-phone items-center justify-center px-4 py-3">
          <Wordmark to="/" />
        </div>
      </header>
      <main className="mx-auto max-w-phone px-4 py-5">{children}</main>
    </div>
  );
}
